"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { trpc } from "@calcom/trpc/react";
import { showToast } from "@calcom/ui/components/toast";

interface WebPushContextProps {
  permission: NotificationPermission;
  isLoading: boolean;
  isSubscribed: boolean;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
}

export const WebPushContext = createContext<WebPushContextProps | null>(null);

interface ProviderProps {
  children: React.ReactNode;
}

async function resolvePushManager(): Promise<PushManager | null> {
  if (!("serviceWorker" in navigator)) return null;
  const registration = await navigator.serviceWorker.register("/service-worker.js");
  if (!("pushManager" in registration)) return null;
  return registration.pushManager;
}

export function WebPushProvider({ children }: ProviderProps) {
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "denied"
  );
  const [pushManager, setPushManager] = useState<PushManager | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const { mutate: addSubscription } =
    trpc.viewer.loggedInViewerRouter.addNotificationsSubscription.useMutation();
  const { mutate: removeSubscription } =
    trpc.viewer.loggedInViewerRouter.removeNotificationsSubscription.useMutation();

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    resolvePushManager()
      .then(async (manager) => {
        if (!manager) return;
        setPushManager(manager);
        const subscription = await manager.getSubscription();
        setIsSubscribed(!!subscription);
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error);
      });
  }, []);

  const contextValue = useMemo(
    () => ({
      permission,
      isLoading,
      isSubscribed,
      subscribe: async () => {
        const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || "";
        if (!vapidPublicKey) {
          showToast("Push-уведомления не настроены на сервере (VAPID)", "error");
          return;
        }

        try {
          setIsLoading(true);
          const newPermission = await Notification.requestPermission();
          setPermission(newPermission);

          if (newPermission !== "granted") {
            showToast("Разрешение на уведомления не получено", "warning");
            return;
          }

          const manager = pushManager ?? (await resolvePushManager());
          if (!manager) {
            showToast("Не удалось зарегистрировать service worker", "error");
            return;
          }
          setPushManager(manager);

          const subscription = await manager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlB64ToUint8Array(vapidPublicKey),
          });
          addSubscription({ subscription: JSON.stringify(subscription) });
          setIsSubscribed(true);
          showToast("Уведомления включены", "success");
        } catch (error) {
          console.error("Failed to subscribe:", error);
          if (
            error instanceof DOMException &&
            error.name === "InvalidAccessError" &&
            error.message.includes("applicationServerKey")
          ) {
            showToast("Неверный VAPID-ключ. Обратитесь к администратору", "error");
          } else {
            showToast("Не удалось включить уведомления", "error");
          }
        } finally {
          setIsLoading(false);
        }
      },
      unsubscribe: async () => {
        const manager = pushManager ?? (await resolvePushManager());
        if (!manager) return;
        try {
          setIsLoading(true);
          const subscription = await manager.getSubscription();
          if (subscription) {
            const subscriptionJson = JSON.stringify(subscription);
            await subscription.unsubscribe();
            removeSubscription({ subscription: subscriptionJson });
            setIsSubscribed(false);
            showToast("Уведомления отключены", "success");
          }
        } catch (error) {
          console.error("Failed to unsubscribe:", error);
          showToast("Не удалось отключить уведомления", "error");
        } finally {
          setIsLoading(false);
        }
      },
    }),
    [permission, isLoading, isSubscribed, pushManager, addSubscription, removeSubscription]
  );

  return <WebPushContext.Provider value={contextValue}>{children}</WebPushContext.Provider>;
}

const urlB64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};
